import type { AsapWindowPolicy, BillingRateInForce, BillingRatePerson, VisitRate } from './api/types.ts';
import { moneyText } from './moneyInput.ts';

/**
 * Company policies in tabs (P-22, Arthur, 28 October 2026, A-#248, A-#250, A-#251), pure so it is tested without a
 * browser: which tab the address names, which residence types each client-visit sub-tab lists, and what is in force,
 * said in words. Who may see or change a policy is unchanged: tier 1 only (the page's gate and the server's).
 */

/**
 * The page's tabs, a fixed key in the address (?tab=): the two client-visit sub-tabs, then Tasks & documents, then
 * Billing rates (165).
 */
export const POLICY_TABS = ['facility', 'community', 'tasks', 'billing'] as const;
export type PolicyTab = (typeof POLICY_TABS)[number];
/** The address's tab, or the first sub-tab where it names none or one this page does not have. */
export function policyTab(value: string | null): PolicyTab {
  return (POLICY_TABS as readonly string[]).includes(value ?? '') ? (value as PolicyTab) : 'facility';
}

/**
 * Arthur's split of the residence types (A-#251): whose visit rate is listed under which sub-tab. Nothing in the data
 * says it (residence_type has no such column), so it is this list. Every type on record is in exactly one of the three;
 * a type this list does not know is shown under "Other types", never hidden.
 */
export const FACILITY_CODES = ['Nursing', 'A.L.P.', 'GroupHome', 'Hospital', 'Psych', 'Assisted Living', 'Memory Care', 'Shelter',
  'Correctional'] as const;
export const COMMUNITY_CODES = ['Apartment', 'House', 'Co-Op', 'Condo', '2 Family', '3 Family', '4 Family', 'Rented Room', 'Other'] as const;
export const OTHER_CODES = ['Vacant Land', 'Commercial', 'Timeshare', 'Unknown'] as const;
export type RateGroup = 'facility' | 'community' | 'other';
export function groupOf(code: string): RateGroup {
  if ((FACILITY_CODES as readonly string[]).includes(code)) return 'facility';
  if ((COMMUNITY_CODES as readonly string[]).includes(code)) return 'community';
  return 'other';
}

export const GROUP_WORDS: Record<RateGroup, string> = {
  facility: 'Facility clients', community: 'Community clients', other: 'Other types',
};

/** "9 residence types. In force today: 4 a year for 8, 12 a year for 1; 1 has no rate." */
export function ratesSummary(types: readonly { current: VisitRate | null }[]): string {
  const by = new Map<number, number>();
  let none = 0;
  for (const t of types) {
    if (!t.current) { none += 1; continue; }
    by.set(t.current.visits_per_year, (by.get(t.current.visits_per_year) ?? 0) + 1);
  }
  const rates = [...by.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]).map(([v, n]) => `${v} a year for ${n}`);
  const head = `${types.length} residence ${types.length === 1 ? 'type' : 'types'}.`;
  const inForce = rates.length ? ` In force today: ${rates.join(', ')}` : ' No rate in force today';
  return `${head}${inForce}${none && rates.length ? `; ${none} ${none === 1 ? 'has' : 'have'} no rate` : ''}.`;
}

/**
 * Billing rates (165): money as the server sends it, a string of dollars and cents, never through a float. "$85.00 an
 * hour"; a missing rate is "No rate" in words, never $0.
 */
export function hourlyWords(usd: string | null): string {
  if (usd === null) return 'No rate';
  const [whole = '0', cents = ''] = usd.split('.');
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${(cents + '00').slice(0, 2)} an hour`;
}
/**
 * What a person may type: dollars, or dollars and cents, up to 8 digits, more than nothing; commas between thousands
 * and "$" welcome (F-100: read by the one reader, src/moneyInput.ts). The server checks again.
 */
export function validHourly(s: string): boolean {
  const t = moneyText(s, { maxDigits: 8 });
  return t !== null && Number(t) > 0;
}
/** The words before recording: "$85.00 an hour for Paralegal / Intake from 1 Nov 2026; time before that keeps its rate." */
export function rateConfirmation(hourly: string, who: string, from: string, say: (d: string) => string): string {
  return `${hourlyWords(hourly.trim())} for ${who} from ${say(from)}; time before that keeps its rate.`;
}
/**
 * A-#392: who the first-rate picker offers: EVERY active person the server listed, by name, each saying whether they
 * already have a rate of their own. Never only the people who have one, or a person could never get a first.
 */
export function rateCandidates(people: readonly BillingRatePerson[], rates: readonly BillingRateInForce[]):
  (BillingRatePerson & { has_own_rate: boolean })[] {
  const own = new Set(rates.filter((r) => r.subject === 'person').map((r) => r.staff_user_id));
  return [...people].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .map((p) => ({ ...p, has_own_rate: own.has(p.id) }));
}

/** "12 roles. 3 have a rate today; 9 have no rate. 1 person has their own rate." */
export function billingSummary(rates: readonly BillingRateInForce[]): string {
  const roles = rates.filter((r) => r.subject === 'role');
  const rated = roles.filter((r) => r.hourly_usd !== null).length;
  const people = rates.filter((r) => r.subject === 'person').length;
  const head = `${roles.length} ${roles.length === 1 ? 'role' : 'roles'}. ${rated} ${rated === 1 ? 'has' : 'have'} a rate today`;
  const none = roles.length - rated;
  return `${head}${none ? `; ${none} ${none === 1 ? 'has' : 'have'} no rate` : ''}.${people
    ? ` ${people} ${people === 1 ? 'person has their' : 'people have their'} own rate.` : ''}`;
}

/** "The ASAP window is 14 days, since 28 Oct 2026; then 30 days from 1 Nov 2026." (dates already in words) */
/**
 * 164 (D-125): one line per drafting role that cannot read a report section, as tier 1 sees it on this page.
 * "Compliance Paralegal cannot read Finance: court reports drafted by this role will refuse."
 */
export function courtReportFaultLine(g: { role_name: string; section: string }): string {
  return `${g.role_name} cannot read ${g.section}: court reports drafted by this role will refuse.`;
}

export function asapSummary(p: Pick<AsapWindowPolicy, 'current' | 'scheduled'>, say: (d: string) => string): string {
  const now = p.current ? `The ASAP window is ${p.current.days} days, since ${say(p.current.effective_from)}` : 'No ASAP window is in force today';
  const next = p.scheduled[0] ? `; then ${p.scheduled[0].days} days from ${say(p.scheduled[0].effective_from)}` : '';
  return `${now}${next}.`;
}
